import React, { useMemo } from "react";
import { graphql, navigate } from "gatsby";
import { getClickableAction } from "./Link";
import PrimaryNavStrip, { Navigation } from "@unum/primary-nav-strip";
import {
  MainNavigationFragmentFragment,
  NavigationFooterFragmentFragment,
  RichTextFragmentFragment,
  TitleWithContentFragmentFragment
} from "../../graphql-types";
import RichText from "./RichText";
import { ClickableAction } from "@unum/clickable";

type Props = {
  data: {
    mainNavigation?: MainNavigationFragmentFragment;
    navigationFooter?: NavigationFooterFragmentFragment;
    navigationPromo?: TitleWithContentFragmentFragment;
    navigationPromoFooter?: RichTextFragmentFragment;
  };
  logoAction?: ClickableAction;
  renderSearch?: boolean;
};

const mapItemsToNavigation = (
  data: MainNavigationFragmentFragment
): Navigation => {
  if (!data) {
    return;
  }

  return {
    label: data.elements.label.value,
    links: data.elements.items.value
      .filter(({ __typename }) =>
        [
          "kontent_item_navigation",
          "kontent_item_internal_link",
          "kontent_item_external_link"
        ].includes(__typename)
      )
      .map((item) => {
        if (item.__typename === "kontent_item_navigation") {
          return {
            label: item.elements.label.value,
            links: item.elements.items.value
              .filter(
                ({ __typename }) => __typename === "kontent_item_navigation"
              )
              .map((navigation) => {
                return {
                  label: navigation.elements.label.value,
                  links: navigation.elements.items.value
                    .map((link) => {
                      if (
                        !(
                          link.__typename === "kontent_item_external_link" ||
                          link.__typename === "kontent_item_internal_link"
                        )
                      ) {
                        return null;
                      }

                      const action = getClickableAction(link);

                      if (!action) {
                        return null;
                      }

                      return {
                        label: link.elements.label.value,
                        action
                      };
                    })
                    .filter(Boolean)
                };
              })
          };
        }
        if (item.__typename === "kontent_item_internal_link") {
          return {
            label: item.elements.label.value,
            action: getClickableAction(item.elements.page.value[0])
          };
        }
        if (item.__typename === "kontent_item_external_link") {
          return {
            label: item.elements.label.value,
            action: getClickableAction(item)
          };
        }
      })
  };
};

const mapItemsToNavigationFooter = (
  data: NavigationFooterFragmentFragment
): Navigation => {
  if (!data) {
    return null;
  }

  return {
    label: data.elements.label.value,
    links: data.elements.items.value.map((link) => {
      if (
        !(
          link.__typename === "kontent_item_external_link" ||
          link.__typename === "kontent_item_internal_link"
        )
      ) {
        return null;
      }

      return {
        label: link.elements.label.value,
        action: getClickableAction(link)
      };
    })
  };
};

const IntegratedPrimaryNavStrip = ({
  data,
  logoAction,
  renderSearch
}: Props) => {
  const menu = useMemo(
    () => mapItemsToNavigation(data.mainNavigation),
    [data.mainNavigation]
  );
  const footerMenu: Navigation = useMemo(
    () => mapItemsToNavigationFooter(data.navigationFooter),
    [data.navigationFooter]
  );

  // { model: "routerLink", to: "/", linkComponent: Link }

  return (
    <PrimaryNavStrip
      menu={menu}
      footerMenu={footerMenu}
      logoAction={logoAction}
      renderSearch={renderSearch}
      onSearch={(term) => {
        if (!term) {
          return;
        }
        navigate(`/search?q=${term}`);
      }}
      promo={
        data.navigationPromo && (
          <PrimaryNavStrip.Promo
            title={data.navigationPromo?.elements.title.value}
            footer={
              data.navigationPromoFooter ? (
                <RichText data={data.navigationPromoFooter} />
              ) : undefined
            }
          >
            <RichText
              settings={{ background: "dark" }}
              data={data.navigationPromo?.elements?.content}
            />
          </PrimaryNavStrip.Promo>
        )
      }
    />
  );
};

export default IntegratedPrimaryNavStrip;

export const query = graphql`
  fragment NavigationFooterFragment on kontent_item_navigation {
    id
    __typename
    elements {
      label {
        value
      }
      items {
        value {
          ...LinkFragment
        }
      }
    }
  }

  fragment MainNavigationFragment on kontent_item_navigation {
    id
    __typename
    elements {
      label {
        value
      }
      items {
        value {
          ...LinkFragment
          ... on kontent_item_navigation {
            id
            __typename
            elements {
              label {
                value
              }
              items {
                value {
                  ... on kontent_item_navigation {
                    id
                    __typename
                    elements {
                      label {
                        value
                      }
                      items {
                        value {
                          ...LinkFragment
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
